import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Libs/firebase-config.mjs';
import BookCard from '../shared/BookCard';

const BooksSection = ({ darkMode }) => {
  const [booksData, setBooksData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBooksData();
  }, []);

  const loadBooksData = async () => {
    try {
      const docRef = doc(db, 'booksSection', 'content');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setBooksData(docSnap.data());
      } else {
        // Fallback to default data
        setBooksData({
          chapter: 'Chapter 09',
          title: 'Published Works',
          books: [
            { 
              title: "Walking in Purpose: A Kenyan Journey", 
              year: "2010",
              author: "Rev. Dr. Peter Kyengo",
              desc: "Discover your God-given calling within the African context" 
            },
            { 
              title: "Grace Upon Grace", 
              year: "2018",
              author: "Rev. Grace Kyengo",
              desc: "A woman's journey in ministry and faith" 
            },
            { 
              title: "Leading with Integrity", 
              year: "2014",
              author: "Rev. Dr. Peter Kyengo",
              desc: "Biblical principles for Kenyan church leadership" 
            },
            { 
              title: "Rooted in Grace", 
              year: "2021",
              author: "Rev. Dr. Peter & Grace Kyengo",
              desc: "Finding stability in Kenya's changing times" 
            }
          ]
        });
      }
    } catch (err) {
      console.error('Error loading books section:', err);
      setError('Failed to load content');
      // Set fallback data on error
      setBooksData({
        chapter: 'Chapter 09',
        title: 'Published Works',
        books: [
          { 
            title: "Walking in Purpose: A Kenyan Journey", 
            year: "2010",
            author: "Rev. Dr. Peter Kyengo",
            desc: "Discover your God-given calling within the African context" 
          },
          { 
            title: "Grace Upon Grace", 
            year: "2018",
            author: "Rev. Grace Kyengo",
            desc: "A woman's journey in ministry and faith" 
          },
          { 
            title: "Leading with Integrity", 
            year: "2014",
            author: "Rev. Dr. Peter Kyengo",
            desc: "Biblical principles for Kenyan church leadership" 
          },
          { 
            title: "Rooted in Grace", 
            year: "2021",
            author: "Rev. Dr. Peter & Grace Kyengo",
            desc: "Finding stability in Kenya's changing times" 
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to determine grid layout based on number of books
  const getGridClass = (bookCount) => {
    if (bookCount === 1) {
      return 'grid grid-cols-1 md:grid-cols-3 gap-8';
    } else if (bookCount === 2) {
      return 'grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto';
    } else if (bookCount === 3) {
      return 'grid grid-cols-1 md:grid-cols-3 gap-8';
    } else {
      return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8';
    }
  };

  // Function to determine which column a book should be in for single book layout
  const getColumnStart = (bookCount, index) => {
    if (bookCount === 1) {
      return 'md:col-start-2'; // Center column (column 2 of 3)
    }
    return '';
  };

  // Show loading state
  if (loading) {
    return (
      <section 
        className={`py-32 px-8 ${darkMode ? 'bg-slate-900' : 'bg-slate-50'} transition-colors duration-300`}
        style={{ minHeight: '400px', visibility: 'visible', opacity: 1 }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              Loading Books...
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Don't render if no data
  if (!booksData) {
    return null;
  }

  const data = booksData;
  const bookCount = data.books?.length || 0;

  return (
    <section 
      className={`py-32 px-8 ${darkMode ? 'bg-slate-900' : 'bg-slate-50'} transition-colors duration-300`}
      style={{ 
        minHeight: '400px',
        visibility: 'visible',
        opacity: 1,
        display: 'block',
        zIndex: 1
      }}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className={`text-sm uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-600'} font-medium mb-4`}>
            {data.chapter}
          </div>
          <h2 className={`text-5xl md:text-6xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            {data.title}
          </h2>
        </motion.div>

        {data.books && data.books.length > 0 ? (
          <div className={getGridClass(bookCount)}>
            {data.books.map((book, index) => (
              <div 
                key={index} 
                className={getColumnStart(bookCount, index)}
              >
                <BookCard 
                  book={book} 
                  index={index} 
                  darkMode={darkMode} 
                />
              </div>
            ))}
          </div>
        ) : (
          <div className={`text-center text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
            No books available
          </div>
        )}
      </div>
    </section>
  );
};

export default BooksSection;